import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as THREE from 'three';

interface ParticleFieldProps {
  mousePosition?: { x: number; y: number };
}

const ParticleField: React.FC<ParticleFieldProps> = ({ mousePosition }) => {
  const points = useRef<THREE.Points>(null);
  const count = 2000;
  const mouse3D = useRef(new THREE.Vector3(0, 0, 0));

  const positions = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    }
    return positions;
  }, []);

  // Update mouse position in 3D space
  useEffect(() => {
    if (mousePosition) {
      const x = ((mousePosition.x / window.innerWidth) - 0.5) * 20;
      const y = -((mousePosition.y / window.innerHeight) - 0.5) * 20;
      mouse3D.current.set(x, y, 0);
    }
  }, [mousePosition]);

  useFrame((state) => {
    if (points.current) {
      // Slow rotation
      points.current.rotation.x = state.clock.elapsedTime * 0.01;
      points.current.rotation.y = state.clock.elapsedTime * 0.02;

      // Magnetic effect - particles move away from mouse
      const positions = points.current.geometry.attributes.position;
      for (let i = 0; i < count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);
        const z = positions.getZ(i);
        
        const particlePos = new THREE.Vector3(x, y, z);
        const distance = particlePos.distanceTo(mouse3D.current);
        const maxDistance = 3;
        const influence = Math.max(0, 1 - distance / maxDistance);
        
        if (influence > 0) {
          const direction = new THREE.Vector3()
            .subVectors(particlePos, mouse3D.current)
            .normalize()
            .multiplyScalar(influence * 0.3);
          
          positions.setXYZ(
            i,
            x + direction.x,
            y + direction.y,
            z + direction.z
          );
        }
      }
      positions.needsUpdate = true;
    }
  });

  return (
    <Points ref={points} positions={positions} stride={3} frustumCulled={false}>
      <PointMaterial
        transparent
        color="#FFFFFF"
        size={0.05}
        sizeAttenuation={true}
        depthWrite={false}
        opacity={0.6}
      />
    </Points>
  );
};

export default ParticleField;

